import { useRef } from 'react'
import { stationImage, stationPhotos, type Station } from './stations'

type Props = {
  station: Station
  photoIndex: number
  onPhotoChange: (index: number) => void
  onOpen?: () => void
  expanded?: boolean
}

export default function StationMedia({ station, photoIndex, onPhotoChange, onOpen, expanded = false }: Props) {
  const photos = stationPhotos(station)
  const photo = photos[photoIndex] ?? photos[0]
  const multiple = photos.length > 1
  const touchStart = useRef<{ x: number; y: number } | null>(null)
  const swiped = useRef(false)
  const move = (direction: number) => onPhotoChange((photoIndex + direction + photos.length) % photos.length)
  const picture = <img src={stationImage(station, expanded ? 1280 : 640, photoIndex)}
    srcSet={`${stationImage(station, 640, photoIndex)} 640w, ${stationImage(station, 1280, photoIndex)} 1280w`}
    sizes={expanded ? '(max-width: 800px) 95vw, 920px' : '(max-width: 700px) 90vw, 43vw'}
    width={photo.width} height={photo.height} loading={expanded ? 'eager' : 'lazy'} decoding="async" alt={photo.alt} />

  return <div className={`station-media${expanded ? ' station-media--expanded' : ''}`} role="group"
    aria-label={`ภาพกิจกรรมฐาน ${station.number} ${station.name}`} aria-roledescription={multiple ? 'สไลด์ภาพ' : undefined}
    onKeyDown={(event) => {
      if (!multiple || !['ArrowLeft', 'ArrowRight'].includes(event.key)) return
      event.preventDefault()
      event.stopPropagation()
      move(event.key === 'ArrowRight' ? 1 : -1)
    }}>
    <div className="station-card-media"
      onTouchStart={(event) => {
        swiped.current = false
        const touch = event.touches[0]
        touchStart.current = event.touches.length === 1 ? { x: touch.clientX, y: touch.clientY } : null
      }}
      onTouchCancel={() => { touchStart.current = null }}
      onTouchEnd={(event) => {
        const start = touchStart.current
        touchStart.current = null
        if (!multiple || !start) return
        const touch = event.changedTouches[0]
        const dx = touch.clientX - start.x
        const dy = touch.clientY - start.y
        if (Math.abs(dx) > 45 && Math.abs(dx) > Math.abs(dy) * 1.5) {
          swiped.current = true
          move(dx < 0 ? 1 : -1)
        }
      }}>
      {onOpen ? <button className="station-photo-open" type="button" aria-haspopup="dialog"
        aria-label={`ขยายภาพฐาน ${station.number} ${station.name} ภาพที่ ${photoIndex + 1}`}
        onClick={(event) => {
          if (swiped.current) { event.preventDefault(); swiped.current = false; return }
          onOpen()
        }}>{picture}</button> : picture}
      {!expanded && <span className="station-index">ฐาน {String(station.number).padStart(2, '0')}</span>}
    </div>
    {multiple && <div className="station-photo-controls">
      <button className="station-photo-arrow" type="button" onClick={() => move(-1)} aria-label={`ภาพก่อนหน้า ฐาน ${station.number}`}>‹</button>
      <div className="station-photo-picker">
        <div className="station-photo-thumbs" role="group" aria-label={`เลือกภาพฐาน ${station.number}`}>
          {photos.map((item, index) => <button type="button" key={item.file} aria-label={`ดูภาพที่ ${index + 1} ฐาน ${station.number}`}
            aria-pressed={index === photoIndex} onClick={() => onPhotoChange(index)}>
            <img src={stationImage(station, 640, index)} width="64" height="48" alt="" loading="lazy" />
          </button>)}
        </div>
        <span className="station-photo-position" aria-live="polite" aria-atomic="true" aria-label={`ภาพที่ ${photoIndex + 1} จาก ${photos.length}`}>
          {String(photoIndex + 1).padStart(2, '0')} <span aria-hidden="true">/</span> {String(photos.length).padStart(2, '0')}
        </span>
      </div>
      <button className="station-photo-arrow" type="button" onClick={() => move(1)} aria-label={`ภาพถัดไป ฐาน ${station.number}`}>›</button>
    </div>}
  </div>
}
